import { FC } from 'react'

import { Skeleton } from 'antd-mobile'
import { cn } from '@/utils'

export type SkeletonType =
  | 'commentList'
  | 'bookList'
  | 'userInfo'
  | 'content'
  | 'contentBtn'
  | 'threadList'
  | 'swiper'
  | 'bookListItem'
  | 'comment'
  | 'group'

interface ISkeletonStateProps {
  className?: string
  type: SkeletonType[]
  commentCount?: number
}

const SkeletonState: FC<ISkeletonStateProps> = ({ className, type, commentCount = 3 }) => {
  const showBookList = type.includes('bookList')
  const showCommentList = type.includes('commentList')
  const showUserInfo = type.includes('userInfo')
  const showContent = type.includes('content')
  const showContentBtn = type.includes('contentBtn')
  const showThreadList = type.includes('threadList')
  const showBookListItem = type.includes('bookListItem')
  const showSwiper = type.includes('swiper')
  const showComment = type.includes('comment')
  const showGroup = type.includes('group')

  return (
    <div className={className}>
      {showUserInfo && (
        <div className={cn('my-[35px] flex items-center px-[37px]')}>
          <Skeleton className={'!h-[50px] !w-[50px] !rounded-full'} animated />
          <div className={cn('ml-[7px] flex flex-col')}>
            <Skeleton className={cn('!h-[19px] !w-[100px] !rounded-[8px]')} animated />
            <Skeleton className={cn('!mt-[5px] !h-[15px] !w-[80px] !rounded-[8px]')} animated />
          </div>
        </div>
      )}

      {showContent && (
        <div className='px-[37px] pb-[35px]'>
          <Skeleton className={cn('!mb-[22px] !h-[30px] !w-[70%] !rounded-[8px]')} animated />
          <div className={cn('flex flex-col gap-[10px]')}>
            <Skeleton className={cn('!h-[26px] w-full !rounded-[8px]')} animated />
            <Skeleton className={cn('!h-[26px] !w-[90%] !rounded-[8px]')} animated />
            <Skeleton className={cn('!h-[26px] !w-[80%] !rounded-[8px]')} animated />
            <Skeleton className={cn('!h-[26px] !w-[60%] !rounded-[8px]')} animated />
          </div>
        </div>
      )}

      {showContentBtn && (
        <div className='px-[37px] pb-[45px]'>
          <Skeleton className={cn('!h-[90px] !w-full !rounded-[15px]')} animated />
        </div>
      )}

      {showBookList && (
        <div className='px-[45px] pb-[49px] [&>div:last-child]:mb-0'>
          {[1, 2].map((v, index) => (
            <div key={`${v}_${index}`} className={cn('mb-[30px]')}>
              <Skeleton className={cn('!h-[120px] !w-full !rounded-[15px]')} animated />
            </div>
          ))}
        </div>
      )}

      {showCommentList && (
        <div className='px-[30px] pb-0 pt-[60px]'>
          {[...Array(commentCount)].map((_, index) => (
            <div key={`${_}_${index}`} className={cn('mb-[40px]', index !== 0 && '[&>div:first-child]:md:hidden')}>
              <div className={cn('flex items-start', index !== 0 && 'md:hidden')}>
                <Skeleton className={cn('!h-[50px] !w-[50px] !rounded-full')} animated />
                <div className={cn('ml-[15px] flex flex-1 flex-col')}>
                  <Skeleton className={cn('!h-[20px] !w-[120px] !rounded-[8px]')} animated />
                  <Skeleton className={cn('!mt-[8px] !h-[16px] !w-[80px] !rounded-[8px]')} animated />
                </div>
              </div>
              <div className={cn('mt-[20px]', index !== 0 && 'md:mt-[15px]')}>
                <Skeleton className={cn('!h-[80px] !w-full !rounded-[8px]')} animated />
              </div>
              <div className={cn('mt-[20px] flex gap-[30px]', index !== 0 && 'md:hidden')}>
                <Skeleton className={cn('!h-[24px] !w-[60px] !rounded-[8px]')} animated />
                <Skeleton className={cn('!h-[24px] !w-[60px] !rounded-[8px]')} animated />
              </div>
            </div>
          ))}
        </div>
      )}

      {showThreadList && (
        <div className='px-[37px] md:px-0'>
          {[...Array(commentCount || Math.floor(window.innerHeight / 200))].map((v, index) => (
            <div key={`${v}_${index}`} className='mb-[22px] rounded-[15px] bg-color-white-trans10 p-[30px]'>
              <Skeleton className='!h-[30px] !w-[30%] !rounded-[8px]' animated />
              <div className='mt-[19px] flex flex-col gap-[8px]'>
                <Skeleton className='!h-[26px] w-[40%] !rounded-[8px]' animated />
                <Skeleton className='!mb-[35px] !h-[26px] w-[80%] !rounded-[8px]' animated />
              </div>
              <div className='flex'>
                <Skeleton className='mr-[19px] !h-[198px] !w-[198px] !rounded-[8px]' animated />
                <Skeleton className='mr-[19px] !h-[198px] !w-[198px] !rounded-[8px]' animated />
                <Skeleton className='!h-[198px] !w-[198px] !rounded-[8px]' animated />
              </div>
            </div>
          ))}
        </div>
      )}

      {showSwiper && (
        <div className='h-[257px] max-h-[257px] w-full rounded-[15px] bg-color-white-trans10 md:h-[690px] md:max-h-[690px]'>
          <Skeleton className='!h-full !w-full !rounded-[15px]' animated />
        </div>
      )}

      {showBookListItem && (
        <div className='gap-[22px] overflow-hidden px-[30px] md:flex md:px-0'>
          {[...Array(commentCount || Math.floor(window.innerHeight / 200))].map((v, index) => (
            <div key={`${v}_${index}`} className='mb-[22px] h-[522px] min-w-[631px] flex-1 rounded-[15px] bg-color-white-trans10 p-[30px] md:mb-0'>
              <div className='mb-[30px] flex'>
                <Skeleton className='!mr-[10px] !h-[45px] !w-[45px] !rounded-full' animated />
                <div className='flex flex-col justify-center'>
                  <Skeleton className='!mb-[6px] !h-[14px] !w-[70px] !rounded-[10px]' animated />
                  <Skeleton className='!h-[14px] !w-[30px] !rounded-[10px]' animated />
                </div>
              </div>
              <div className='mb-[30px] h-[82px]'>
                <Skeleton className='!mb-[6px] !h-[20px] !w-[50%] !rounded-[10px]' animated />
                <Skeleton className='!mb-[6px] !h-[20px] !w-[70%] !rounded-[10px]' animated />
              </div>
              <div className='mt-[30px] h-[271px] rounded-[20px] bg-discover-card-content-bg-color p-[30px]'>
                <div className='flex gap-[22px] overflow-hidden'>
                  <Skeleton className='!h-[210px] !w-[149px] flex-shrink-0 !rounded-[8px]' animated />
                  <Skeleton className='!h-[210px] !w-[149px] flex-shrink-0 !rounded-[8px]' animated />
                  <Skeleton className='!h-[210px] !w-[149px] flex-shrink-0 !rounded-[8px]' animated />
                  <Skeleton className='!h-[210px] !w-[149px] flex-shrink-0 !rounded-[8px]' animated />
                  <Skeleton className='!h-[210px] !w-[149px] flex-shrink-0 !rounded-[8px]' animated />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showComment && (
        <div className='px-[30px] md:px-0'>
          <div className='flex gap-[22px] overflow-hidden'>
            {[1, 2, 3].map((v, index) => (
              <div key={`${v}_${index}`} className='h-[522px] min-w-[631px] flex-1 rounded-[15px] bg-color-white-trans10 p-[30px]'>
                <div className='mb-[30px] flex'>
                  <Skeleton className='!mr-[10px] !h-[45px] !w-[45px] !rounded-full' animated />
                  <div className='flex flex-col justify-center'>
                    <Skeleton className='!mb-[6px] !h-[14px] !w-[70px] !rounded-[10px]' animated />
                    <Skeleton className='!h-[14px] !w-[30px] !rounded-[10px]' animated />
                  </div>
                </div>
                <div className='mb-[30px] h-[82px]'>
                  <Skeleton className='!mb-[6px] !h-[20px] !w-[50%] !rounded-[10px]' animated />
                  <Skeleton className='!mb-[6px] !h-[20px] !w-[70%] !rounded-[10px]' animated />
                </div>
                <div className='mt-[30px] h-[271px] rounded-[20px] bg-discover-card-content-bg-color p-[30px]'>
                  <div className='flex'>
                    <Skeleton className='!h-[210px] !w-[149px] !rounded-[8px]' animated />
                    <div className='ml-[19px] flex flex-1 flex-col'>
                      <Skeleton className='!h-[26px] !w-full !rounded-[8px]' animated />
                      <Skeleton className='!mt-[10px] !h-[26px] !w-[80%] !rounded-[8px]' animated />
                      <Skeleton className='!mt-[10px] !h-[26px] !w-[60%] !rounded-[8px]' animated />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showGroup && (
        <div className='px-[30px] md:px-0'>
          <div className='flex gap-[22px] overflow-hidden'>
            {[1, 2, 3, 4].map((v, index) => (
              <div key={`${v}_${index}`} className='h-[370px] min-w-[299px] rounded-[15px] bg-color-white-trans10 p-[30px]'>
                <div className='flex flex-col items-center'>
                  <Skeleton className='!h-[104px] !w-[104px] flex-shrink-0 !rounded-full' animated />
                  <div className='flex !w-full flex-col items-center'>
                    <Skeleton className='!mt-[52px] !h-[24px] !w-full !rounded-[8px]' animated />
                    <Skeleton className='!mt-[10px] !h-[20px] !w-[70%] !rounded-[8px]' animated />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default SkeletonState
